/**
 * Progress Dashboard Scene
 * Scientific data terminal for viewing learning progress
 * Bioluminescent microscopy aesthetic
 */

import Phaser from 'phaser';
import { AssessmentSystem, AdaptiveDifficultySystem, AnalyticsSystem } from '@game/systems';
import { MasteryLevel } from '@/types';
import { CONCEPTS } from '@/data/concepts';

// Deep bioluminescent palette
const COLORS = {
  deepOcean: 0x040d1a,
  darkBlue: 0x0a1628,
  midBlue: 0x0f2847,
  accentCyan: 0x00d4ff,
  accentTeal: 0x00ffd4,
  accentGreen: 0x00ff88,
  accentPurple: 0xb366ff,
  accentGold: 0xffd700,
  accentPink: 0xff66b3,
  textPrimary: 0xe8f4f8,
  textSecondary: 0x8ba4b4,
  textMuted: 0x4a6070,
};

const MASTERY_COLORS: Record<MasteryLevel, number> = {
  [MasteryLevel.NOT_INTRODUCED]: 0x2d3748,
  [MasteryLevel.INTRODUCED]: 0x4a6070,
  [MasteryLevel.PRACTICING]: 0xecc94b,
  [MasteryLevel.DEVELOPING]: 0x48bb78,
  [MasteryLevel.PROFICIENT]: 0x00d4ff,
  [MasteryLevel.MASTERED]: 0xb366ff,
};

const MASTERY_LABELS: Record<MasteryLevel, string> = {
  [MasteryLevel.NOT_INTRODUCED]: 'Not Started',
  [MasteryLevel.INTRODUCED]: 'Introduced',
  [MasteryLevel.PRACTICING]: 'Practicing',
  [MasteryLevel.DEVELOPING]: 'Developing',
  [MasteryLevel.PROFICIENT]: 'Proficient',
  [MasteryLevel.MASTERED]: 'Mastered',
};

interface FloatingParticle {
  graphics: Phaser.GameObjects.Graphics;
  x: number;
  y: number;
  speed: number;
  size: number;
  alpha: number;
  color: number;
}

export class ProgressDashboardScene extends Phaser.Scene {
  private assessmentSystem!: AssessmentSystem;
  private difficultySystem!: AdaptiveDifficultySystem;
  private analyticsSystem!: AnalyticsSystem;
  private particles: FloatingParticle[] = [];
  private masteryOrbs: Phaser.GameObjects.Container[] = [];
  private progressRing?: Phaser.GameObjects.Graphics;
  private progressRingAngle = 0;

  constructor() {
    super({ key: 'ProgressDashboardScene' });
  }

  create(): void {
    // Initialize systems
    this.assessmentSystem = new AssessmentSystem(this);
    this.difficultySystem = new AdaptiveDifficultySystem(this);
    this.analyticsSystem = new AnalyticsSystem(this);

    // Create layered background
    this.createBackground();
    this.createFloatingParticles();
    this.createDNAHelix();

    // Create UI elements with staggered animation
    this.time.delayedCall(100, () => this.createHeader());
    this.time.delayedCall(200, () => this.createProgressRing());
    this.time.delayedCall(400, () => this.createStatsPanel());
    this.time.delayedCall(500, () => this.createMasteryOrbs());
    this.time.delayedCall(700, () => this.createExportPanel());
    this.createBackButton();
  }

  override update(_time: number, delta: number): void {
    // Animate floating particles
    this.updateParticles(delta);

    // Animate progress ring glow
    this.progressRingAngle += delta * 0.001;
    if (this.progressRing) {
      const glowIntensity = 0.3 + Math.sin(this.progressRingAngle * 2) * 0.1;
      this.progressRing.setAlpha(glowIntensity + 0.5);
    }
  }

  private createBackground(): void {
    const { width, height } = this.cameras.main;
    const bg = this.add.graphics();

    // Deep ocean base
    bg.fillStyle(COLORS.deepOcean, 1);
    bg.fillRect(0, 0, width, height);

    // Vertical gradient layers
    for (let i = 0; i < 30; i++) {
      const ratio = i / 30;
      const y = ratio * height;
      const alpha = 0.02 + Math.sin(ratio * Math.PI) * 0.025;
      bg.fillStyle(COLORS.midBlue, alpha);
      bg.fillRect(0, y, width, height / 30 + 1);
    }

    // Large radial glow from center-top
    const centerX = width / 2;
    for (let i = 20; i >= 0; i--) {
      const radius = (width * 0.5) * (i / 20);
      const alpha = 0.012 * (1 - i / 20);
      bg.fillStyle(COLORS.accentCyan, alpha);
      bg.fillCircle(centerX, height * 0.25, radius);
    }

    // Corner ambient glows
    const glowSpots = [
      { x: 0, y: 0, color: COLORS.accentPurple, size: 300 },
      { x: width, y: height, color: COLORS.accentTeal, size: 350 },
      { x: 0, y: height, color: COLORS.accentCyan, size: 250 },
      { x: width, y: 0, color: COLORS.accentGreen, size: 200 },
    ];

    glowSpots.forEach((glow) => {
      for (let i = 20; i >= 0; i--) {
        const radius = (i / 20) * glow.size;
        const alpha = 0.008 * (1 - i / 20);
        bg.fillStyle(glow.color, alpha);
        bg.fillCircle(glow.x, glow.y, radius);
      }
    });

    // Subtle scan lines overlay
    const scanLines = this.add.graphics();
    scanLines.lineStyle(1, COLORS.accentCyan, 0.015);
    for (let y = 0; y < height; y += 4) {
      scanLines.lineBetween(0, y, width, y);
    }
  }

  private createFloatingParticles(): void {
    const { width, height } = this.cameras.main;

    for (let i = 0; i < 40; i++) {
      const graphics = this.add.graphics();
      const particle: FloatingParticle = {
        graphics,
        x: Math.random() * width,
        y: Math.random() * height,
        speed: 0.2 + Math.random() * 0.4,
        size: 1 + Math.random() * 2.5,
        alpha: 0.1 + Math.random() * 0.3,
        color: [COLORS.accentCyan, COLORS.accentTeal, COLORS.accentGreen, COLORS.accentPurple][
          Math.floor(Math.random() * 4)
        ],
      };
      this.particles.push(particle);
      this.drawParticle(particle);
    }
  }

  private drawParticle(particle: FloatingParticle): void {
    particle.graphics.clear();

    // Outer glow
    particle.graphics.fillStyle(particle.color, particle.alpha * 0.3);
    particle.graphics.fillCircle(particle.x, particle.y, particle.size * 2);

    // Core
    particle.graphics.fillStyle(particle.color, particle.alpha);
    particle.graphics.fillCircle(particle.x, particle.y, particle.size);
  }

  private updateParticles(delta: number): void {
    const { height } = this.cameras.main;

    this.particles.forEach((particle) => {
      particle.y -= particle.speed * delta * 0.03;
      if (particle.y < -10) {
        particle.y = height + 10;
        particle.x = Math.random() * this.cameras.main.width;
      }
      // Gentle horizontal drift
      particle.x += Math.sin(particle.y * 0.01) * 0.3;
      this.drawParticle(particle);
    });
  }

  private createDNAHelix(): void {
    const { width, height } = this.cameras.main;
    const helix = this.add.graphics();
    helix.setAlpha(0.06);

    // Draw DNA helix on right edge
    const helixX = width - 60;
    const amplitude = 30;
    const wavelength = 80;

    for (let y = 0; y < height; y += 3) {
      const offset1 = Math.sin(y / wavelength * Math.PI * 2) * amplitude;
      const offset2 = Math.sin((y / wavelength * Math.PI * 2) + Math.PI) * amplitude;

      helix.fillStyle(COLORS.accentCyan, 0.5);
      helix.fillCircle(helixX + offset1, y, 2);
      helix.fillStyle(COLORS.accentPurple, 0.5);
      helix.fillCircle(helixX + offset2, y, 2);

      // Connecting rungs
      if (y % 15 === 0) {
        helix.lineStyle(1, COLORS.accentTeal, 0.3);
        helix.lineBetween(helixX + offset1, y, helixX + offset2, y);
      }
    }

    // Left edge helix (mirrored)
    const helixX2 = 60;
    for (let y = 0; y < height; y += 3) {
      const offset1 = Math.sin((y + 40) / wavelength * Math.PI * 2) * amplitude;
      const offset2 = Math.sin(((y + 40) / wavelength * Math.PI * 2) + Math.PI) * amplitude;

      helix.fillStyle(COLORS.accentTeal, 0.4);
      helix.fillCircle(helixX2 + offset1, y, 2);
      helix.fillStyle(COLORS.accentGreen, 0.4);
      helix.fillCircle(helixX2 + offset2, y, 2);

      if (y % 15 === 0) {
        helix.lineStyle(1, COLORS.accentCyan, 0.2);
        helix.lineBetween(helixX2 + offset1, y, helixX2 + offset2, y);
      }
    }
  }

  private createHeader(): void {
    const { width } = this.cameras.main;
    const container = this.add.container(width / 2, 50);

    // Title with glow
    const title = this.add.text(0, 0, 'LEARNING PROGRESS', {
      fontFamily: '"Courier New", monospace',
      fontSize: '38px',
      fontStyle: 'bold',
      color: '#00d4ff',
    }).setOrigin(0.5);
    title.setStroke('#001a2e', 4);
    title.setShadow(0, 0, '#00d4ff', 15, true, true);

    // Decorative line
    const line = this.add.graphics();
    line.lineStyle(2, COLORS.accentCyan, 0.4);
    line.lineBetween(-180, 25, 180, 25);

    // Small diamond accents
    line.fillStyle(COLORS.accentCyan, 0.6);
    line.fillRect(-4, 23, 8, 8);
    line.setAngle(45);

    container.add([title, line]);
    container.setAlpha(0);

    this.tweens.add({
      targets: container,
      alpha: 1,
      y: 55,
      duration: 500,
      ease: 'Back.out',
    });
  }

  private createProgressRing(): void {
    const { width } = this.cameras.main;
    const centerX = width / 2;
    const centerY = 190;
    const radius = 70;

    const container = this.add.container(centerX, centerY);

    // Background ring
    const bgRing = this.add.graphics();
    bgRing.lineStyle(8, COLORS.darkBlue, 0.8);
    bgRing.strokeCircle(0, 0, radius);

    // Progress ring
    this.progressRing = this.add.graphics();
    const progress = this.assessmentSystem.getOverallProgress();
    const endAngle = -Math.PI / 2 + progress * Math.PI * 2;

    this.progressRing.lineStyle(8, COLORS.accentCyan, 0.9);
    this.progressRing.beginPath();
    this.progressRing.arc(0, 0, radius, -Math.PI / 2, endAngle, false);
    this.progressRing.strokePath();

    // Glow effect
    const glowRing = this.add.graphics();
    glowRing.lineStyle(16, COLORS.accentCyan, 0.15);
    glowRing.beginPath();
    glowRing.arc(0, 0, radius, -Math.PI / 2, endAngle, false);
    glowRing.strokePath();

    // Center content
    const percentText = this.add.text(0, -8, `${Math.round(progress * 100)}%`, {
      fontFamily: '"Courier New", monospace',
      fontSize: '32px',
      fontStyle: 'bold',
      color: '#00d4ff',
    }).setOrigin(0.5);
    percentText.setShadow(0, 0, '#00d4ff', 10, true, true);

    const labelText = this.add.text(0, 22, 'MASTERY', {
      fontFamily: 'Georgia, serif',
      fontSize: '12px',
      color: '#8ba4b4',
      letterSpacing: 4,
    }).setOrigin(0.5);

    // Cell membrane decoration around ring
    const membrane = this.add.graphics();
    membrane.lineStyle(1, COLORS.accentTeal, 0.2);
    for (let i = 0; i < 36; i++) {
      const angle = (i / 36) * Math.PI * 2;
      const x1 = Math.cos(angle) * (radius + 12);
      const y1 = Math.sin(angle) * (radius + 12);
      const x2 = Math.cos(angle) * (radius + 18);
      const y2 = Math.sin(angle) * (radius + 18);
      membrane.lineBetween(x1, y1, x2, y2);
    }

    container.add([bgRing, glowRing, this.progressRing, membrane, percentText, labelText]);
    container.setAlpha(0);
    container.setScale(0.8);

    this.tweens.add({
      targets: container,
      alpha: 1,
      scale: 1,
      duration: 600,
      ease: 'Back.out',
    });
  }

  private createStatsPanel(): void {
    const panelX = 50;
    const panelY = 290;
    const panelWidth = 280;
    const panelHeight = 200;

    const container = this.add.container(panelX, panelY);

    // Panel background with glow border
    const bg = this.add.graphics();
    bg.fillStyle(COLORS.darkBlue, 0.85);
    bg.fillRoundedRect(0, 0, panelWidth, panelHeight, 12);
    bg.lineStyle(1, COLORS.accentCyan, 0.4);
    bg.strokeRoundedRect(0, 0, panelWidth, panelHeight, 12);

    // Corner accents
    const corners = this.add.graphics();
    corners.lineStyle(2, COLORS.accentCyan, 0.6);
    // Top-left
    corners.lineBetween(0, 15, 0, 0);
    corners.lineBetween(0, 0, 15, 0);
    // Top-right
    corners.lineBetween(panelWidth - 15, 0, panelWidth, 0);
    corners.lineBetween(panelWidth, 0, panelWidth, 15);
    // Bottom-left
    corners.lineBetween(0, panelHeight - 15, 0, panelHeight);
    corners.lineBetween(0, panelHeight, 15, panelHeight);
    // Bottom-right
    corners.lineBetween(panelWidth - 15, panelHeight, panelWidth, panelHeight);
    corners.lineBetween(panelWidth, panelHeight - 15, panelWidth, panelHeight);

    // Title
    const title = this.add.text(20, 15, 'SESSION DATA', {
      fontFamily: '"Courier New", monospace',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#00d4ff',
      letterSpacing: 2,
    });

    // Stats
    const stats = this.analyticsSystem.getSessionAnalytics();
    const diffStats = this.difficultySystem.getStats();

    const statsData = [
      { label: 'Levels Completed', value: stats.levelCompletes.toString(), color: COLORS.accentGreen },
      {
        label: 'Classifications',
        value: `${stats.classificationsCorrect}/${stats.classificationsAttempted}`,
        color: COLORS.accentTeal,
      },
      {
        label: 'Accuracy',
        value:
          stats.classificationsAttempted > 0
            ? `${Math.round((stats.classificationsCorrect / stats.classificationsAttempted) * 100)}%`
            : '—',
        color: COLORS.accentCyan,
      },
      { label: 'Difficulty', value: diffStats.difficultyLabel, color: COLORS.accentPurple },
      { label: 'Time Played', value: this.formatTime(stats.duration), color: COLORS.accentGold },
    ];

    container.add([bg, corners, title]);

    statsData.forEach((stat, i) => {
      const y = 50 + i * 28;

      // Label
      const label = this.add.text(20, y, stat.label, {
        fontFamily: 'Georgia, serif',
        fontSize: '13px',
        color: '#8ba4b4',
      });

      // Value with colored indicator
      const indicator = this.add.graphics();
      indicator.fillStyle(stat.color, 0.8);
      indicator.fillCircle(panelWidth - 80, y + 7, 4);

      const value = this.add
        .text(panelWidth - 20, y, stat.value, {
          fontFamily: '"Courier New", monospace',
          fontSize: '14px',
          fontStyle: 'bold',
          color: '#e8f4f8',
        })
        .setOrigin(1, 0);

      container.add([label, indicator, value]);
    });

    container.setAlpha(0);

    this.tweens.add({
      targets: container,
      alpha: 1,
      x: panelX + 10,
      duration: 500,
      ease: 'Power2.out',
    });
  }

  private createMasteryOrbs(): void {
    const { width, height } = this.cameras.main;
    const panelX = width - 330;
    const panelY = 120;
    const panelWidth = 290;
    const panelHeight = height - 190;

    const container = this.add.container(panelX, panelY);

    // Panel background
    const bg = this.add.graphics();
    bg.fillStyle(COLORS.darkBlue, 0.85);
    bg.fillRoundedRect(0, 0, panelWidth, panelHeight, 12);
    bg.lineStyle(1, COLORS.accentPurple, 0.3);
    bg.strokeRoundedRect(0, 0, panelWidth, panelHeight, 12);

    // Title
    const title = this.add.text(20, 15, 'CONCEPT MASTERY', {
      fontFamily: '"Courier New", monospace',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#b366ff',
      letterSpacing: 2,
    });

    container.add([bg, title]);

    // Create mastery orbs for each concept
    const conceptsToShow = CONCEPTS.slice(0, 6);
    const orbRadius = 22;
    const startY = 60;
    const rowHeight = 60;

    conceptsToShow.forEach((concept, i) => {
      const mastery = this.assessmentSystem.getConceptMastery(concept.id);
      const color = MASTERY_COLORS[mastery];
      const label = MASTERY_LABELS[mastery];
      const y = startY + i * rowHeight;

      // Create orb container
      const orbContainer = this.add.container(35, y + orbRadius);

      // Outer glow (based on mastery)
      const glowIntensity = mastery / MasteryLevel.MASTERED;
      if (glowIntensity > 0) {
        const glow = this.add.graphics();
        for (let g = 3; g >= 0; g--) {
          glow.fillStyle(color, 0.1 * (1 - g / 3) * glowIntensity);
          glow.fillCircle(0, 0, orbRadius + 8 + g * 4);
        }
        orbContainer.add(glow);
      }

      // Main orb
      const orb = this.add.graphics();
      orb.fillStyle(COLORS.darkBlue, 1);
      orb.fillCircle(0, 0, orbRadius);
      orb.lineStyle(2, color, 0.8);
      orb.strokeCircle(0, 0, orbRadius);

      // Fill based on mastery (like cell filling)
      if (mastery > 0) {
        const fillAngle = (mastery / MasteryLevel.MASTERED) * Math.PI * 2;
        orb.fillStyle(color, 0.3);
        orb.slice(0, 0, orbRadius - 2, -Math.PI / 2, -Math.PI / 2 + fillAngle, false);
        orb.fillPath();
      }

      // Inner highlight
      orb.fillStyle(0xffffff, 0.15);
      orb.fillCircle(-6, -6, 6);

      orbContainer.add(orb);

      // Concept text
      const conceptText = concept.text.length > 35 ? concept.text.substring(0, 32) + '...' : concept.text;
      const text = this.add.text(70, y + 8, conceptText, {
        fontFamily: 'Georgia, serif',
        fontSize: '11px',
        color: '#e8f4f8',
        wordWrap: { width: 180 },
      });

      // Mastery label
      const masteryText = this.add.text(70, y + 32, label, {
        fontFamily: '"Courier New", monospace',
        fontSize: '10px',
        color: Phaser.Display.Color.IntegerToColor(color).rgba,
      });

      container.add([orbContainer, text, masteryText]);
      this.masteryOrbs.push(orbContainer);

      // Subtle pulse animation for mastered concepts
      if (mastery === MasteryLevel.MASTERED) {
        this.tweens.add({
          targets: orbContainer,
          scale: 1.05,
          duration: 1500,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    });

    // More concepts indicator
    if (CONCEPTS.length > 6) {
      const moreText = this.add
        .text(panelWidth / 2, panelHeight - 25, `+${CONCEPTS.length - 6} more concepts`, {
          fontFamily: 'Georgia, serif',
          fontSize: '11px',
          fontStyle: 'italic',
          color: '#4a6070',
        })
        .setOrigin(0.5);
      container.add(moreText);
    }

    container.setAlpha(0);

    this.tweens.add({
      targets: container,
      alpha: 1,
      x: panelX - 10,
      duration: 500,
      ease: 'Power2.out',
    });
  }

  private createExportPanel(): void {
    const { height } = this.cameras.main;
    const panelX = 50;
    const panelY = height - 175;
    const panelWidth = 280;
    const panelHeight = 135;

    const container = this.add.container(panelX, panelY);

    // Panel background
    const bg = this.add.graphics();
    bg.fillStyle(COLORS.darkBlue, 0.85);
    bg.fillRoundedRect(0, 0, panelWidth, panelHeight, 12);
    bg.lineStyle(1, COLORS.accentTeal, 0.3);
    bg.strokeRoundedRect(0, 0, panelWidth, panelHeight, 12);

    // Title
    const title = this.add.text(20, 15, 'EXPORT DATA', {
      fontFamily: '"Courier New", monospace',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#00ffd4',
      letterSpacing: 2,
    });

    const subtitle = this.add.text(20, 38, 'Share progress with your teacher', {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      fontStyle: 'italic',
      color: '#8ba4b4',
    });

    container.add([bg, title, subtitle]);

    // Export buttons side by side
    this.createHexButton(container, 30, 70, 'JSON', () => this.exportData('json'));
    this.createHexButton(container, 145, 70, 'CSV', () => this.exportData('csv'));

    container.setAlpha(0);

    this.tweens.add({
      targets: container,
      alpha: 1,
      y: panelY - 10,
      duration: 500,
      ease: 'Power2.out',
    });
  }

  private createHexButton(
    parent: Phaser.GameObjects.Container,
    x: number,
    y: number,
    label: string,
    onClick: () => void
  ): void {
    const btnWidth = 100;
    const btnHeight = 45;

    const btnContainer = this.add.container(x, y);

    // Hexagonal-ish button shape
    const bg = this.add.graphics();
    this.drawHexButton(bg, btnWidth, btnHeight, COLORS.midBlue, COLORS.accentTeal, 0.5);

    const text = this.add
      .text(btnWidth / 2, btnHeight / 2, label, {
        fontFamily: '"Courier New", monospace',
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#00ffd4',
      })
      .setOrigin(0.5);

    btnContainer.add([bg, text]);
    btnContainer.setSize(btnWidth, btnHeight);
    btnContainer.setInteractive({ useHandCursor: true });

    btnContainer.on('pointerover', () => {
      bg.clear();
      this.drawHexButton(bg, btnWidth, btnHeight, 0x1a4050, COLORS.accentTeal, 0.9);
      text.setColor('#00ffd4');
      text.setShadow(0, 0, '#00ffd4', 8, true, true);
    });

    btnContainer.on('pointerout', () => {
      bg.clear();
      this.drawHexButton(bg, btnWidth, btnHeight, COLORS.midBlue, COLORS.accentTeal, 0.5);
      text.setColor('#00ffd4');
      text.setShadow(0, 0, '', 0, false, false);
    });

    btnContainer.on('pointerdown', onClick);

    parent.add(btnContainer);
  }

  private drawHexButton(
    graphics: Phaser.GameObjects.Graphics,
    width: number,
    height: number,
    fillColor: number,
    strokeColor: number,
    strokeAlpha: number
  ): void {
    const indent = 8;
    graphics.fillStyle(fillColor, 0.9);
    graphics.beginPath();
    graphics.moveTo(indent, 0);
    graphics.lineTo(width - indent, 0);
    graphics.lineTo(width, height / 2);
    graphics.lineTo(width - indent, height);
    graphics.lineTo(indent, height);
    graphics.lineTo(0, height / 2);
    graphics.closePath();
    graphics.fillPath();

    graphics.lineStyle(1, strokeColor, strokeAlpha);
    graphics.beginPath();
    graphics.moveTo(indent, 0);
    graphics.lineTo(width - indent, 0);
    graphics.lineTo(width, height / 2);
    graphics.lineTo(width - indent, height);
    graphics.lineTo(indent, height);
    graphics.lineTo(0, height / 2);
    graphics.closePath();
    graphics.strokePath();
  }

  private createBackButton(): void {
    const container = this.add.container(30, 30);

    const glow = this.add.graphics();
    glow.fillStyle(COLORS.accentCyan, 0.08);
    glow.fillRoundedRect(-3, -3, 96, 42, 10);

    const bg = this.add.graphics();
    bg.fillStyle(COLORS.darkBlue, 0.95);
    bg.fillRoundedRect(0, 0, 90, 36, 8);
    bg.lineStyle(1, COLORS.accentCyan, 0.5);
    bg.strokeRoundedRect(0, 0, 90, 36, 8);

    const text = this.add
      .text(45, 18, '← BACK', {
        fontFamily: '"Courier New", monospace',
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#00d4ff',
      })
      .setOrigin(0.5);

    container.add([glow, bg, text]);
    container.setSize(90, 36);
    container.setInteractive({ useHandCursor: true });
    container.setDepth(1000);

    container.on('pointerover', () => {
      glow.clear();
      glow.fillStyle(COLORS.accentCyan, 0.15);
      glow.fillRoundedRect(-3, -3, 96, 42, 10);
      bg.clear();
      bg.fillStyle(COLORS.darkBlue, 1);
      bg.fillRoundedRect(0, 0, 90, 36, 8);
      bg.lineStyle(2, COLORS.accentCyan, 0.8);
      bg.strokeRoundedRect(0, 0, 90, 36, 8);
      text.setColor('#00ffd4');
    });

    container.on('pointerout', () => {
      glow.clear();
      glow.fillStyle(COLORS.accentCyan, 0.08);
      glow.fillRoundedRect(-3, -3, 96, 42, 10);
      bg.clear();
      bg.fillStyle(COLORS.darkBlue, 0.95);
      bg.fillRoundedRect(0, 0, 90, 36, 8);
      bg.lineStyle(1, COLORS.accentCyan, 0.5);
      bg.strokeRoundedRect(0, 0, 90, 36, 8);
      text.setColor('#00d4ff');
    });

    container.on('pointerdown', () => {
      this.scene.start('MenuScene');
    });
  }

  private exportData(format: 'json' | 'csv'): void {
    try {
      this.assessmentSystem.downloadExport(format);
      this.showNotification(`Progress exported as ${format.toUpperCase()}`, true);
    } catch (error) {
      console.error('Export failed:', error);
      this.showNotification('Export failed', false);
    }
  }

  private showNotification(message: string, success: boolean): void {
    const { width, height } = this.cameras.main;

    const notification = this.add.container(width / 2, height - 40);

    const color = success ? COLORS.accentGreen : 0xff4444;
    const bgColor = success ? 0x1a4030 : 0x401a1a;

    const bg = this.add.graphics();
    bg.fillStyle(bgColor, 0.95);
    bg.fillRoundedRect(-140, -22, 280, 44, 8);
    bg.lineStyle(1, color, 0.6);
    bg.strokeRoundedRect(-140, -22, 280, 44, 8);

    const icon = success ? '✓' : '✕';
    const text = this.add
      .text(0, 0, `${icon}  ${message}`, {
        fontFamily: '"Courier New", monospace',
        fontSize: '14px',
        color: Phaser.Display.Color.IntegerToColor(color).rgba,
      })
      .setOrigin(0.5);

    notification.add([bg, text]);
    notification.setAlpha(0);
    notification.setY(height);

    this.tweens.add({
      targets: notification,
      alpha: 1,
      y: height - 60,
      duration: 400,
      ease: 'Back.out',
      onComplete: () => {
        this.time.delayedCall(2500, () => {
          this.tweens.add({
            targets: notification,
            alpha: 0,
            y: height - 40,
            duration: 300,
            onComplete: () => notification.destroy(),
          });
        });
      },
    });
  }

  private formatTime(seconds: number): string {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins < 60) return `${mins}m ${secs}s`;
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return `${hours}h ${remMins}m`;
  }

  shutdown(): void {
    this.assessmentSystem?.destroy();
    this.difficultySystem?.destroy();
    this.analyticsSystem?.destroy();
    this.particles = [];
    this.masteryOrbs = [];
  }
}
